import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X, Plus, Trash2, User as UserIcon } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { User, UserRole } from '@/types/user'

const AVATAR_MAX_SIZE_BYTES = 5 * 1024 * 1024
const AVATAR_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  expertise: z.array(z.string()).optional(),
  interests: z.array(z.string()).optional(),
  goals: z.array(z.string()).optional(),
  maxMentees: z.number().min(1).max(20).optional(),
  isAvailable: z.boolean().optional(),
})

type ProfileFormData = z.infer<typeof profileSchema>

interface ProfileEditFormProps {
  user: User
  onSave: (data: ProfileFormData, pictureFile?: File) => Promise<void>
  onRemovePicture: () => Promise<string | undefined>
  onCancel: () => void
  isLoading?: boolean
}

const ProfileEditForm: React.FC<ProfileEditFormProps> = ({
  user,
  onSave,
  onRemovePicture,
  onCancel,
  isLoading = false,
}) => {
  const [newExpertise, setNewExpertise] = useState('')
  const [newInterest, setNewInterest] = useState('')
  const [newGoal, setNewGoal] = useState('')
  const [pictureFile, setPictureFile] = useState<File | undefined>()
  const [picturePreviewUrl, setPicturePreviewUrl] = useState(user.profile?.profilePicture)
  const [pictureError, setPictureError] = useState<string | undefined>()
  const [hasUploadedPicture, setHasUploadedPicture] = useState(!!user.profile?.hasUploadedPicture)
  const [isRemovingPicture, setIsRemovingPicture] = useState(false)
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
      }
    }
  }, [])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      bio: user.profile?.bio || '',
      expertise: user.profile?.expertise || [],
      interests: user.profile?.interests || [],
      goals: user.profile?.goals || [],
      maxMentees: user.profile?.maxMentees || 5,
      isAvailable: user.profile?.isAvailable ?? true,
    },
  })

  const expertise = watch('expertise') || []
  const interests = watch('interests') || []
  const goals = watch('goals') || []

  const addExpertise = () => {
    if (newExpertise.trim() && !expertise.includes(newExpertise.trim())) {
      setValue('expertise', [...expertise, newExpertise.trim()], { shouldDirty: true })
      setNewExpertise('')
    }
  }

  const removeExpertise = (index: number) => {
    setValue('expertise', expertise.filter((_, i) => i !== index), { shouldDirty: true })
  }

  const addInterest = () => {
    if (newInterest.trim() && !interests.includes(newInterest.trim())) {
      setValue('interests', [...interests, newInterest.trim()], { shouldDirty: true })
      setNewInterest('')
    }
  }

  const removeInterest = (index: number) => {
    setValue('interests', interests.filter((_, i) => i !== index), { shouldDirty: true })
  }

  const addGoal = () => {
    if (newGoal.trim() && !goals.includes(newGoal.trim())) {
      setValue('goals', [...goals, newGoal.trim()], { shouldDirty: true })
      setNewGoal('')
    }
  }

  const removeGoal = (index: number) => {
    setValue('goals', goals.filter((_, i) => i !== index), { shouldDirty: true })
  }

  const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''

    if (!file) {
      return
    }

    if (!AVATAR_ALLOWED_TYPES.includes(file.type)) {
      setPictureError('Unsupported file type. Please choose a JPEG, PNG, or WebP image.')
      return
    }

    if (file.size > AVATAR_MAX_SIZE_BYTES) {
      setPictureError('File is too large. The maximum size is 5MB.')
      return
    }

    setPictureError(undefined)

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
    }
    const previewUrl = URL.createObjectURL(file)
    objectUrlRef.current = previewUrl

    setPictureFile(file)
    setPicturePreviewUrl(previewUrl)
  }

  const handleRemovePicture = async () => {
    setIsRemovingPicture(true)
    setPictureError(undefined)
    try {
      const fallbackUrl = await onRemovePicture()

      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }

      setPictureFile(undefined)
      setPicturePreviewUrl(fallbackUrl)
      setHasUploadedPicture(false)
    } catch (error) {
      setPictureError(error instanceof Error ? error.message : 'Failed to remove picture. Please try again.')
    } finally {
      setIsRemovingPicture(false)
    }
  }

  const onSubmit = async (data: ProfileFormData) => {
    try {
      await onSave(data, pictureFile)
    } catch (error) {
      console.error('Error saving profile:', error)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Edit Profile</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isLoading}
        >
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardBody>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Profile Picture */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-primary-100 flex items-center justify-center flex-shrink-0">
              {picturePreviewUrl ? (
                <img
                  src={picturePreviewUrl}
                  alt="Profile picture preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <UserIcon className="w-10 h-10 text-primary-600" />
              )}
            </div>
            <div>
              <label htmlFor="profilePicture" className="block text-sm font-medium text-gray-700 mb-1">
                Profile Picture
              </label>
              <input
                id="profilePicture"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePictureChange}
                disabled={isLoading}
                className="block text-sm text-gray-600"
              />
              {hasUploadedPicture && !pictureFile && (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  className="mt-2"
                  onClick={handleRemovePicture}
                  disabled={isLoading || isRemovingPicture}
                  isLoading={isRemovingPicture}
                >
                  Remove picture
                </Button>
              )}
              {pictureError && (
                <p className="mt-1 text-sm text-red-600">{pictureError}</p>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <Input
                {...register('firstName')}
                placeholder="Enter first name"
                error={errors.firstName?.message}
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <Input
                {...register('lastName')}
                placeholder="Enter last name"
                error={errors.lastName?.message}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bio
            </label>
            <textarea
              {...register('bio')}
              rows={3}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 resize-none"
              placeholder="Tell others about yourself..."
              disabled={isLoading}
            />
            {errors.bio && (
              <p className="mt-1 text-sm text-red-600">{errors.bio.message}</p>
            )}
          </div>

          {/* Mentor-specific fields */}
          {user.role === UserRole.MENTOR && (
            <>
              {/* Expertise Areas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expertise Areas
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newExpertise}
                    onChange={(e) => setNewExpertise(e.target.value)}
                    placeholder="Add expertise area"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addExpertise()
                      }
                    }}
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addExpertise}
                    disabled={!newExpertise.trim() || isLoading}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {expertise.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full"
                    >
                      {skill}
                      <button
                        type="button"
                        onClick={() => removeExpertise(index)}
                        className="ml-2 text-primary-600 hover:text-primary-800"
                        disabled={isLoading}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Mentorship Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Mentees
                  </label>
                  <Input
                    type="number"
                    {...register('maxMentees', { valueAsNumber: true })}
                    min="1"
                    max="20"
                    placeholder="5"
                    error={errors.maxMentees?.message}
                    disabled={isLoading}
                  />
                </div>
                <div className="flex items-center space-x-3 pt-6">
                  <input
                    type="checkbox"
                    {...register('isAvailable')}
                    id="isAvailable"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    disabled={isLoading}
                  />
                  <label htmlFor="isAvailable" className="text-sm font-medium text-gray-700">
                    Available for new mentees
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Mentee-specific fields */}
          {user.role === UserRole.MENTEE && (
            <>
              {/* Interests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interests
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newInterest}
                    onChange={(e) => setNewInterest(e.target.value)}
                    placeholder="Add interest"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addInterest()
                      }
                    }}
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addInterest}
                    disabled={!newInterest.trim() || isLoading}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {interests.map((interest, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                    >
                      {interest}
                      <button
                        type="button"
                        onClick={() => removeInterest(index)}
                        className="ml-2 text-gray-500 hover:text-gray-700"
                        disabled={isLoading}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Learning Goals */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Learning Goals
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newGoal}
                    onChange={(e) => setNewGoal(e.target.value)}
                    placeholder="Add learning goal"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addGoal()
                      }
                    }}
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addGoal}
                    disabled={!newGoal.trim() || isLoading}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {goals.map((goal, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm text-gray-700">• {goal}</span>
                      <button
                        type="button"
                        onClick={() => removeGoal(index)}
                        className="text-gray-400 hover:text-gray-600"
                        disabled={isLoading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={(!isDirty && !pictureFile) || isLoading}
              isLoading={isLoading}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  )
}

export default ProfileEditForm